import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, CalendarDays, HeartHandshake, ShieldCheck, MapPin,
  CheckCircle2, Car, Briefcase, UserCheck, GraduationCap, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";

const SEO_TITLE = "Housekeeping Jobs in Denton & Waco, TX | QuadScrub Careers";
const SEO_DESC =
  "Join QuadScrub as a professional housekeeper serving student apartments in Denton and Waco, Texas. Flexible scheduling, meaningful work, and respectful teams. Apply today.";

function usePageMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const ensureMeta = (name, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("content");
      return { el, prev, created };
    };
    const desc = ensureMeta("description");
    const ogTitle = ensureMeta("og:title", "property");
    const ogDesc = ensureMeta("og:description", "property");
    const twTitle = ensureMeta("twitter:title");
    const twDesc = ensureMeta("twitter:description");
    desc.el.setAttribute("content", description);
    ogTitle.el.setAttribute("content", title);
    ogDesc.el.setAttribute("content", description);
    twTitle.el.setAttribute("content", title);
    twDesc.el.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      [desc, ogTitle, ogDesc, twTitle, twDesc].forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };
  }, [title, description]);
}

const BENEFITS = [
  {
    icon: CalendarDays,
    title: "Flexible scheduling",
    body: "Pick the days and routes that fit your life — most cleaners work 15–30 hours a week.",
  },
  {
    icon: HeartHandshake,
    title: "Meaningful work",
    body: "You're not just cleaning — you're giving busy college students a calm place to come home to.",
  },
  {
    icon: ShieldCheck,
    title: "Professional standards",
    body: "Clear checklists, photo-verified reports, supportive leads, and real feedback loops.",
  },
  {
    icon: MapPin,
    title: "Local routes in TX",
    body: "Serving student apartments near UNT & TWU in Denton and Baylor in Waco.",
  },
];

const LOOKING_FOR = [
  { icon: UserCheck, title: "Reliability", body: "You show up on time and finish what you start." },
  { icon: Briefcase, title: "Experience or willingness to learn", body: "Prior cleaning experience is great; we also train motivated beginners." },
  { icon: Car, title: "Reliable transportation", body: "A dependable vehicle for traveling between apartments." },
  { icon: Sparkles, title: "Professionalism", body: "Respectful, discreet, and detail-oriented." },
  { icon: GraduationCap, title: "Comfort in student apartments", body: "You're at ease moving through shared student living spaces." },
];

const EMPTY = {
  name: "", email: "", phone: "", city: "", availability: "",
  experience: "", motivation: "", referral_source: "",
  has_transportation: false, work_authorized: false, background_check_consent: false,
};

export default function Careers() {
  usePageMeta(SEO_TITLE, SEO_DESC);

  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { application_id }

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function validate() {
    if (!form.name || form.name.trim().length < 2) return "Please enter your full name.";
    if (!form.email) return "Please enter your email.";
    if (!form.phone || form.phone.trim().length < 7) return "Please enter a valid phone number.";
    if (!form.city) return "Please select your service area.";
    if (!form.availability.trim()) return "Please share your availability.";
    if (!form.experience.trim()) return "Tell us about your cleaning experience (or willingness to learn).";
    if (!form.motivation || form.motivation.trim().length < 10) return "Please share why you want to join (at least 10 characters).";
    if (!form.work_authorized) return "Please confirm you are legally authorized to work in the US.";
    if (!form.background_check_consent) return "Please agree to a future background check.";
    return null;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/careers/apply", form);
      setSubmitted(data);
      toast.success("Application received — we'll be in touch soon.");
    } catch (ex) {
      const detail = formatError(ex.response?.data?.detail) || ex.message || "Could not submit application";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="careers-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 opacity-20"
             style={{backgroundImage: "radial-gradient(circle at 20% 20%, #ffffff33, transparent 40%), radial-gradient(circle at 80% 60%, #ffffff22, transparent 45%)"}} />
        <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <Badge className="bg-white/15 text-white border-white/20 mb-4" variant="outline">
              Careers · Denton & Waco, TX
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Make a Difference in Students' Homes.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
              QuadScrub is hiring reliable, professional housekeepers to care for college-student apartments
              near <span className="font-semibold text-white">UNT in Denton</span> and <span className="font-semibold text-white">Baylor in Waco</span>. Join a small, respectful team that treats cleaning as the craft it is.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90" data-testid="hero-apply-btn">
                <a href="#apply">Apply Now <ArrowRight size={16} className="ml-1" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 bg-transparent">
                <a href="#what-we-look-for">What we look for</a>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-2 hidden lg:block">
            <div className="rounded-3xl bg-white/10 border border-white/20 backdrop-blur p-8 space-y-4">
              <div className="flex items-center gap-3"><CheckCircle2 className="text-white/90" size={18} /> <span className="text-sm">Weekly, biweekly, and monthly routes</span></div>
              <div className="flex items-center gap-3"><CheckCircle2 className="text-white/90" size={18} /> <span className="text-sm">Checklist-driven, photo-verified work</span></div>
              <div className="flex items-center gap-3"><CheckCircle2 className="text-white/90" size={18} /> <span className="text-sm">Supportive leads and clear expectations</span></div>
              <div className="flex items-center gap-3"><CheckCircle2 className="text-white/90" size={18} /> <span className="text-sm">Great fit for working parents, retirees, & students</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <Badge variant="outline" className="mb-3">Why QuadScrub</Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary">A better place to clean for a living.</h2>
          <p className="mt-3 text-muted-foreground">
            We're building the housekeeping standard for student apartments in Texas. That starts with hiring people who actually enjoy the work — and treating them well.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-border/60 bg-background p-6 hover:shadow-md transition-shadow"
                 data-testid={`benefit-${title.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon size={18} />
              </div>
              <div className="font-display font-semibold text-primary">{title}</div>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE LOOK FOR */}
      <section id="what-we-look-for" className="bg-muted/40 border-y border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Badge variant="outline" className="mb-3">What we look for</Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary">You don't need a resume full of cleaning jobs.</h2>
            <p className="mt-3 text-muted-foreground">
              What matters most is that you're dependable, careful, and comfortable walking into a student's home like it's your own kitchen. We'll teach the rest.
            </p>
          </div>
          <ul className="lg:col-span-3 space-y-4">
            {LOOKING_FOR.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-4 bg-background rounded-2xl border border-border/60 p-5">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <div>
                  <div className="font-display font-semibold text-primary">{title}</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-3">Apply</Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary">Tell us about yourself.</h2>
          <p className="mt-3 text-muted-foreground">
            Takes about 3 minutes. We review every application and reply within a few business days.
          </p>
        </div>

        {submitted ? (
          <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="careers-confirmation">
            <div className="w-14 h-14 rounded-full bg-emerald-500 text-white mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="font-display text-2xl font-bold text-primary">Application received!</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              Thanks, {form.name || "friend"}. Our team reviews every application personally. You'll hear from a QuadScrub lead by email or phone within a few business days.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              <strong>Note:</strong> submitting an application does <em>not</em> create a Cleaner Dashboard account.
              Accounts are set up only after review and onboarding.
            </p>
            <p className="mt-5 text-[11px] text-muted-foreground">Reference: {submitted.application_id?.slice(0, 8)}</p>
            <div className="mt-6">
              <Button asChild variant="outline" data-testid="back-home-btn">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit}
                className="bg-background border border-border/60 rounded-3xl p-6 sm:p-8 space-y-5 shadow-sm"
                data-testid="careers-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name *">
                <Input value={form.name} onChange={(e) => upd("name", e.target.value)}
                       placeholder="Jane Doe" required data-testid="careers-name" />
              </Field>
              <Field label="Email *">
                <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)}
                       placeholder="you@email.com" required data-testid="careers-email" />
              </Field>
              <Field label="Phone *">
                <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)}
                       placeholder="(555) 123-4567" required data-testid="careers-phone" />
              </Field>
              <Field label="Service area *">
                <Select value={form.city} onValueChange={(v) => upd("city", v)}>
                  <SelectTrigger data-testid="careers-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Denton">Denton, TX (UNT / TWU)</SelectItem>
                    <SelectItem value="Waco">Waco, TX (Baylor)</SelectItem>
                    <SelectItem value="Other">Nearby area</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Availability *" help="Which days & time windows work for you?">
              <Textarea value={form.availability} onChange={(e) => upd("availability", e.target.value)}
                        rows={3} placeholder="e.g. Mon/Wed/Fri 9am–3pm, flexible Saturdays"
                        required data-testid="careers-availability" />
            </Field>

            <Field label="Cleaning experience *" help="Prior roles, cleaning your own home at a high standard, formal training — all count.">
              <Textarea value={form.experience} onChange={(e) => upd("experience", e.target.value)}
                        rows={3} placeholder="Share your relevant experience (or your willingness to learn)."
                        required data-testid="careers-experience" />
            </Field>

            <Field label="Why do you want to join QuadScrub? *">
              <Textarea value={form.motivation} onChange={(e) => upd("motivation", e.target.value)}
                        rows={4} placeholder="Tell us a little about yourself and why this role appeals to you."
                        required data-testid="careers-motivation" />
            </Field>

            <Field label="How did you hear about us?">
              <Input value={form.referral_source} onChange={(e) => upd("referral_source", e.target.value)}
                     placeholder="Friend, Google, Facebook, a current cleaner…"
                     data-testid="careers-referral" />
            </Field>

            <div className="space-y-3 rounded-2xl bg-muted/40 border border-border/60 p-5">
              <CheckboxRow id="has_transportation" checked={form.has_transportation}
                           onChange={(v) => upd("has_transportation", v)}
                           label="I have reliable transportation to travel between apartment communities."
                           testid="careers-transportation" />
              <CheckboxRow id="work_authorized" checked={form.work_authorized}
                           onChange={(v) => upd("work_authorized", v)}
                           label="I am legally authorized to work in the United States. *"
                           testid="careers-workauth" />
              <CheckboxRow id="background_check_consent" checked={form.background_check_consent}
                           onChange={(v) => upd("background_check_consent", v)}
                           label="I agree to a future background check if offered a position. *"
                           testid="careers-bgcheck" />
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-relaxed px-4 py-3">
              <strong>Please note:</strong> submitting this application does not create a Cleaner
              Dashboard account. Accounts are created only after review and onboarding by the QuadScrub team.
            </div>

            <Button type="submit" size="lg" disabled={busy}
                    className="w-full bg-primary hover:bg-primary/90" data-testid="careers-submit">
              {busy ? "Submitting…" : "Submit Application"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Questions? Email <a href="mailto:hello@quadscrub.com" className="underline">hello@quadscrub.com</a>
            </p>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-primary mb-1.5">{label}</div>
      {children}
      {help ? <div className="text-[11px] text-muted-foreground mt-1">{help}</div> : null}
    </label>
  );
}

function CheckboxRow({ id, checked, onChange, label, testid }) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={testid} className="mt-0.5" />
      <label htmlFor={id} className="text-sm text-primary leading-snug cursor-pointer">
        {label}
      </label>
    </div>
  );
}
